import { IUserAuthResponse } from 'types'
import { callApi, ENDPOINTS } from 'utils/api/callApi'
import { FP_ENDPOINTS } from 'apps/fleet-planner/config'
import { IUpdateUserSettingsResponse } from 'apps/fleet-planner/types/entity'
import { storeUserData } from 'utils/userAuth'

/**
 * Gets user session details from their userId
 * @method GET
 */
export const getUserSessionDetails = (userId: number): Promise<any> => {
  return callApi({
    method: 'GET',
    endpoint: ENDPOINTS.fleetPlannerUsers,
    group: `${userId}/session_details`,
    includeXHeaderUserId: false,
  })
}

/**
 * Logs the user in via email and password
 * @method POST
 */
export const credLogin = (args: {
  email?: string
  password?: string
}): Promise<IUserAuthResponse> => {
  const { email, password } = args || {}
  return callApi({
    endpoint: ENDPOINTS.signIn,
    method: 'POST',
    body: { userLogin: { email, password } },
  })
}

/**
 * Logs the user in via auth token (param in URL)
 * @method POST
 */
export const authTokenLogin = (token: string): Promise<IUserAuthResponse> => {
  return callApi({
    endpoint: ENDPOINTS.signIn,
    method: 'POST',
    body: { userLogin: { confirmationToken: token } },
  })
}

export const authTokenReturningUserLogin = (
  resetPasswordToken: string
): Promise<IUserAuthResponse> => {
  return callApi({
    endpoint: ENDPOINTS.signIn,
    method: 'POST',
    body: { userLogin: { resetPasswordToken } },
  })
}

/**
 * Updates user password. Needed before proceeding in user onboarding.
 * @method PUT
 */
export const setUserPassword = (payload: {
  confirmationToken: string
  password: string
  phone: string | number | null
  cellPhone: string | number | null
}): Promise<void> => {
  const { cellPhone, confirmationToken, password, phone } = payload

  return callApi({
    endpoint: ENDPOINTS.password,
    method: 'PUT',
    body: {
      user: {
        confirmationToken,
        password,
        passwordConfirmation: password,
        phone,
        cellPhone,
      },
    },
  })
}

/**
 * Requests a login link for an email address
 * @method POST
 */
export const requestLoginLink = (email: string): Promise<void> => {
  return callApi({
    endpoint: ENDPOINTS.parentUsers,
    method: 'POST',
    group: 'fleet_planner_signup',
    body: { email },
  })
}

/**
 * Adds signup intents to user's settings
 * @method PUT
 */
export const addUsersignupIntents = (
  signupIntents: string[]
): Promise<IUpdateUserSettingsResponse> => {
  return callApi({
    endpoint: FP_ENDPOINTS.current_user,
    group: 'settings',
    method: 'PUT',
    body: {
      user: {
        settings: {
          signupIntents,
        },
      },
    },
  })
}

/**
 * Triggers a GET call and sets user information cookie
 */
export const setAuthCookies = async (res: any): Promise<void> => {
  const { children = [] } = res || {}
  await storeUserData({ ...res, app: 'fleet-planner' })
  if (children.length) {
    const [user] = children || []
    const { id: userId } = user || {}
    const userSessionDetailsRes = await getUserSessionDetails(userId)
    await storeUserData({ ...userSessionDetailsRes, app: 'fleet-planner' })
  }
}
